import { useState } from "react"
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query"
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router"
import { ArrowLeft, ArrowRight, ArrowUpRight, Check, CheckCheck, ShieldCheck, Users } from "lucide-react"
import { toast } from "sonner"
import type { AutomationDetail, FlowNode } from "@vienna/contracts"
import { NODE_LABELS } from "@vienna/contracts"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { Switch } from "@/components/ui/switch"
import { AutoHeading } from "@/components/automations/primitives"
import { api, ApiError } from "@/lib/api"
import { automationQuery } from "@/lib/queries"
import { useCan, useWorkspaceContext } from "@/lib/session"

export const Route = createFileRoute("/w/$workspace/automations/$automationId/publish")({ component: Publish })

function describe(n: FlowNode): string {
  const c = n.config
  switch (c.kind) {
    case "trigger":
      return c.trigger.kind === "comment_keyword" ? `Listens for ${c.trigger.match.keywords.join(", ") || "keywords"} on ${c.trigger.scope === "selected_posts" ? "selected posts" : "all posts"}.` : c.trigger.kind === "new_message" ? "Starts on a new conversation." : "Started by a teammate."
    case "condition":
      return `Checks for ${c.match.keywords.join(", ") || "keywords"}.`
    case "public_reply":
    case "private_message":
    case "message":
      return c.text ? `“${c.text.slice(0, 90)}${c.text.length > 90 ? "…" : ""}”` : "No text."
    case "wait":
      return `Waits up to ${c.timeoutMinutes >= 60 ? `${Math.round(c.timeoutMinutes / 60)} hours` : `${c.timeoutMinutes} minutes`} for a reply.`
    case "handover":
      return `${c.reason}. Automation pauses for that contact.`
    case "tag":
      return `Adds the tag “${c.tagName}”.`
    case "end":
      return c.note || "Ends the run."
  }
}

/** Screen 21. */
function Publish() {
  const { workspace } = useWorkspaceContext()
  const can = useCan()
  const { automationId } = Route.useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const automation = useSuspenseQuery(automationQuery(workspace.slug, automationId)).data
  const [reviewed, setReviewed] = useState(false)
  const [activate, setActivate] = useState(true)
  const params = { workspace: workspace.slug }
  const draft = automation.draft
  const live = automation.live
  const publish = useMutation({
    mutationFn: () => api<AutomationDetail>(`/w/${workspace.slug}/automations/${automationId}/publish`, { method: "POST", body: JSON.stringify({ reviewed: true, activate }) }),
    onSuccess: async (d) => {
      await queryClient.invalidateQueries({ queryKey: ["workspace", workspace.slug, "automations"] })
      toast.success(`${d.name} v${d.liveVersion} published`, { description: activate ? `New eligible events use v${d.liveVersion}. Existing runs keep their version.` : "Version saved without starting new runs." })
      await navigate({ to: "/w/$workspace/automations", params })
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.problem.title : "Could not publish"),
  })
  const changes = draft
    ? draft.definition.nodes.filter((n) => {
        const before = live?.definition.nodes.find((l) => l.id === n.id)
        return !before || JSON.stringify(before.config) !== JSON.stringify(n.config) || before.title !== n.title
      })
    : []
  const nextVersion = draft?.number ?? (live?.number ?? 0) + 1
  return (
    <div className="page-wrap auto-page">
      <AutoHeading
        title="Ready for your next conversation"
        description={draft ? `Review ${automation.name} v${nextVersion} before publishing.` : `${automation.name} has no draft to publish.`}
        back={
          <Button variant="ghost" size="sm" className="mb-3 -ml-2" asChild>
            <Link to="/w/$workspace/automations/$automationId" params={{ ...params, automationId }}>
              <ArrowLeft />
              {automation.name}
            </Link>
          </Button>
        }
      />
      <div className="auto-two-col">
        <Card>
          <CardHeader>
            <div className="mb-3 flex items-center gap-2">
              <Badge variant="outline">
                {draft?.validationPassedAt ? <Check className="size-3" /> : null}
                {draft?.validationPassedAt ? "Validation passed" : "Validation not run"}
              </Badge>
              <Badge variant="outline">
                {draft?.simulationPassedAt ? <Check className="size-3" /> : null}
                {draft?.simulationPassedAt ? "Simulation passed" : "Simulation not run"}
              </Badge>
            </div>
            <CardTitle>What changes in v{nextVersion}</CardTitle>
            <CardDescription>{draft ? `Draft saved · ${changes.length} step${changes.length === 1 ? "" : "s"} differ from ${live ? `live v${live.number}` : "nothing (first version)"}` : "No draft"}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {(changes.length ? changes : (draft?.definition.nodes ?? [])).map((n) => (
                <div className="flex gap-3 border-b pb-6 last:border-0" key={n.id}>
                  <Check className="mt-0.5 size-4 shrink-0" />
                  <div>
                    <h3 className="font-medium">{n.title || NODE_LABELS[n.config.kind]}</h3>
                    <p className="mt-1 text-sm leading-6 text-muted-foreground">{describe(n)}</p>
                  </div>
                </div>
              ))}
              <div className="flex gap-3">
                <Check className="mt-0.5 size-4 shrink-0" />
                <div>
                  <h3 className="font-medium">Contact entry</h3>
                  <p className="mt-1 text-sm leading-6 text-muted-foreground">{draft?.definition.entryScope === "per_post" ? "Once per contact, per post." : draft?.definition.entryScope === "always" ? "Every matching event starts a run." : "Once per contact, per stable trigger across selected posts."}</p>
                </div>
              </div>
            </div>
            <div className="mt-2 grid grid-cols-[1fr_auto_1fr] items-center gap-4 rounded-lg bg-muted/50 p-5">
              <div>
                <Badge variant="outline">{live ? `Live v${live.number}` : "No live version"}</Badge>
                <p className="mt-3 text-xs text-muted-foreground">{live ? "Existing runs continue" : "Nothing is running yet"}</p>
              </div>
              <ArrowRight className="size-4 text-muted-foreground" />
              <div>
                <Badge>Publish v{nextVersion}</Badge>
                <p className="mt-3 text-xs text-muted-foreground">New eligible events</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="self-start">
          <CardHeader>
            <CardTitle>Publication settings</CardTitle>
            <CardDescription>{automation.connectionLabel ?? "No channel"} · {automation.provider === "messenger" ? "Messenger" : "Instagram"}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <Label htmlFor="activate-flow">Activate for new events</Label>
                <p className="mt-2 text-xs leading-5 text-muted-foreground">Start new eligible conversations with v{nextVersion} immediately after publication.</p>
              </div>
              <Switch id="activate-flow" checked={activate} onCheckedChange={setActivate} />
            </div>
            <Separator />
            <div className="space-y-3 text-sm">
              <p className="flex items-center gap-2">
                <ShieldCheck className="size-4" />
                Existing runs keep their version
              </p>
              <p className="flex items-center gap-2">
                <CheckCheck className="size-4" />
                Completed steps are preserved
              </p>
              <p className="flex items-center gap-2">
                <Users className="size-4" />
                Entry history stays intact
              </p>
            </div>
            <Separator />
            <div className="flex items-start gap-3">
              <Checkbox id="publish-reviewed" checked={reviewed} onCheckedChange={(value) => setReviewed(value === true)} />
              <Label htmlFor="publish-reviewed" className="text-sm leading-6 font-normal">
                I’ve reviewed the messages and the customer journey.
              </Label>
            </div>
            <Button className="w-full" disabled={!reviewed || !draft || !can("publish") || publish.isPending} onClick={() => publish.mutate()}>
              Publish v{nextVersion}
              <ArrowUpRight />
            </Button>
            <p className="text-xs leading-5 text-muted-foreground">{can("publish") ? "Publication is recorded in the audit log." : "Publishing needs the marketing or admin role."}</p>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
